/** Canvas background color — a personal view preference shared by every canvas
 *  (crochet + grid editors, both viewers, shared links, /try; never the
 *  /embed/* iframe: it doesn't use the canvas tile class, and getCanvasColor()
 *  reports Paper there, so the backdrop marks and ruler keep their Paper ink
 *  too — see isEmbedDocument()). Persisted in
 *  localStorage and applied as `--canvas-bg` on <html>, which style.css maps to
 *  the `bg-canvas` utility on BENTO_CANVAS_CLASSES. Independent of the
 *  interface theme: dark mode never darkens the canvas and nothing drawn on it
 *  changes color. CSS-only, so exports never include it (the Pixi canvas is
 *  transparent). Mirrors bento-radius-preference.ts (preview vs. persist).
 *  Default: Paper. */
import { isEmbedDocument } from '@/utils/embed-document';
import { normalizeStitchColorInput } from '@/utils/stitch-color';

const STORAGE_KEY = 'stitchpress-canvas-color';
export const CANVAS_COLOR_CSS_VAR = '--canvas-bg';

export const CANVAS_COLOR_PRESETS = [
    { id: 'paper', hex: '#fcf9f4' },
    { id: 'white', hex: '#ffffff' },
    { id: 'linen', hex: '#efe9df' },
    { id: 'stone', hex: '#b9b4ad' },
    { id: 'charcoal', hex: '#3a3836' },
    { id: 'night', hex: '#1f1e1d' },
] as const;

/** Paper — the light --background; also the `:root` --canvas-bg in style.css. */
export const DEFAULT_CANVAS_COLOR = '#fcf9f4';

type Listener = (hex: string) => void;
const listeners = new Set<Listener>();

/** The persisted preference, or Paper when unset/invalid — and always Paper in
 *  the /embed/* iframe, which renders inside the composer's paper page. */
export function getCanvasColor(): string {
    if (isEmbedDocument()) return DEFAULT_CANVAS_COLOR;
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        return (stored != null ? normalizeStitchColorInput(stored) : null) ?? DEFAULT_CANVAS_COLOR;
    } catch {
        return DEFAULT_CANVAS_COLOR;
    }
}

/** Apply + notify without persisting — the custom editor's per-frame drag. */
export function previewCanvasColor(hex: string): void {
    const norm = normalizeStitchColorInput(hex);
    if (norm == null) return;
    applyRoot(norm);
    for (const l of listeners) l(norm);
}

/** Persist, apply and notify. Invalid input is ignored. */
export function setCanvasColor(hex: string): void {
    const norm = normalizeStitchColorInput(hex);
    if (norm == null) return;
    try {
        window.localStorage.setItem(STORAGE_KEY, norm);
    } catch {
        // Ignore storage failures (private mode, etc.).
    }
    applyRoot(norm);
    for (const l of listeners) l(norm);
}

export function onCanvasColorChange(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** Apply the persisted preference to <html>. Call once at startup. */
export function initCanvasColor(): void {
    applyRoot(getCanvasColor());
}

function applyRoot(hex: string): void {
    if (typeof document === 'undefined') return;
    document.documentElement.style.setProperty(CANVAS_COLOR_CSS_VAR, hex);
}
